import { useEffect, useRef } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select, Switch } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { NameI18nFields } from './NameI18nFields'
import { policyOptionLabel } from '@/shared/lib/planName'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  BILLING_KINDS,
  DELIVERY_METHODS,
  PLAN_STATES,
  createPlan,
  fetchPolicies,
  planDraftBody,
  updatePlan,
  type Plan,
  type PlanFormValues,
} from './catalogApi'

interface PlanFormModalProps {
  open: boolean
  /** `null` = yangi paket */
  plan: Plan | null
  onClose: () => void
}

type FormValues = PlanFormValues

const BYTES_IN_GB = 1024 ** 3

export function PlanFormModal({ open, plan, onClose }: PlanFormModalProps) {
  const { t, i18n } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  const policies = useQuery({
    queryKey: queryKeys.catalog.policies,
    queryFn: fetchPolicies,
    enabled: open,
  })

  /**
   * "Qo'shimcha paket" belgisi YOQILISHIDAN OLDINGI siyosat.
   *
   * ⚠️ Belgi qaytarib o'chirilganda maydonni tiklash uchun yagona manba:
   * `Form` ning o'zida qiymat allaqachon `null` ga aylangan bo'ladi.
   * Oyna har ochilganda qaytadan to'ldiriladi (pastdagi `useEffect`) —
   * aks holda oldingi paketning siyosati keyingisiga sizib o'tardi.
   */
  const previousPolicyId = useRef<string | null>(null)

  useEffect(() => {
    if (!open) return
    previousPolicyId.current = plan?.policyId ?? null
    form.setFieldsValue(
      plan
        ? {
            name: plan.name,
            nameI18n: plan.nameI18n,
            policyId: plan.policyId,
            addon: plan.addon,
            billingKind: plan.billingKind,
            price: plan.price,
            // Kvota baytda saqlanadi, lekin operator GB'da o'ylaydi.
            // Aylantirish FAQAT shu yerda — formada bayt so'rash
            // "10737418240" kabi terilishi mumkin bo'lmagan songa olib kelardi
            quotaGb: plan.quotaBytes === null ? null : plan.quotaBytes / BYTES_IN_GB,
            // ⚠️ Oldindan to'ldirilmasa forma vaqt kvotasini "bo'sh" deb
            // ochar va saqlash uni TOZALAB yuborardi — sotilgan "1 soat"
            // kartasi cheksizga aylanardi.
            quotaSeconds: plan.quotaSeconds,
            validityDays: plan.validityDays,
            deliveryMethod: plan.deliveryMethod,
            isPublic: plan.isPublic,
            state: plan.state,
          }
        : {
            // ⚠️ Yangi paket standart holda ASOSIY tarif — ya'ni siyosat
            // majburiy. Teskarisi (`addon: true`) unutilgan siyosatni yana
            // jimgina o'tkazib yuborardi.
            addon: false,
            billingKind: 'PREPAID_TRAFFIC',
            deliveryMethod: 'PPPOE',
            isPublic: true,
            state: 'ACTIVE',
            price: money(0, currency),
          },
    )
  }, [open, plan, form, currency])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // ⚠️ Tana KOMPONENTDA yig'ilmaydi: `planDraftBody` ni qorovul
      // CHAQIRIB o'lchaydi, ya'ni tushib qolgan maydon naqsh qidiruvidan
      // emas, HAQIQIY so'rovdan ko'rinadi.
      const draft = planDraftBody(values, plan, money(0, currency))
      return plan ? updatePlan(plan.id, draft) : createPlan(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.plans })
      // Ma'lumotnoma keshi ham eskiradi — abonent yaratish oynasidagi
      // paketlar ro'yxati shu yerdan o'qiydi
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.planIndex })
      form.resetFields()
      onClose()
    },
  })

  const addon = Form.useWatch('addon', form) ?? false

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={plan ? t('catalog.plan.editTitle') : t('catalog.plan.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="name"
          label={t('catalog.plan.name')}
          rules={[{ required: true, message: t('tariffs.form.validation.nameRequired') }]}
        >
          <Input autoFocus />
        </Form.Item>

        <NameI18nFields />

        {/*
          ⚠️ NIYAT — SIYOSATDAN OLDIN so'raladi va bu ataylab.

          Ilgari bu yerda savol umuman yo'q edi: siyosat bo'sh qolsa forma
          KO'K «izoh» chiqarardi, ya'ni unutilgan siyosatni "shunday ham
          bo'ladi" deb tasdiqlardi. Jonli `tarif-15m` shu yo'l bilan
          yaratildi, sotuvga chiqdi va abonent u bilan ulana olmadi.

          Endi operator o'zi aytadi, panel esa taxmin qilmaydi.
        */}
        <Form.Item
          name="addon"
          label={t('catalog.plan.addon')}
          extra={t('catalog.plan.addonHint')}
          valuePropName="checked"
        >
          {/*
            Yoqilganda tanlangan siyosat DARHOL tozalanadi: bloklangan
            maydonda qolib ketgan nom "u baribir qo'llanadi" degan yolg'on
            ko'rinish berardi (yuborishda u baribir tashlanadi).

            ⚠️ O'CHIRILGANDA ESA QAYTARILADI — bu tuzatilgan nuqson.

            Ilgari faqat tozalash bor edi va u QAYTARIB BO'LMAYDIGAN
            edi: operator belgini bosib ko'rib, darhol qaytarsa,
            siyosat maydoni BO'SH qolardi. Ekranda bu "panel tanlovimni
            o'chirib yubordi" bo'lib ko'rinadi va operator qaysi siyosat
            turganini eslashi kerak bo'ladi — tahrirlanayotgan paketda
            esa uni umuman bilmasligi mumkin.

            O'lchandi (Staging, 2026-08-16): "tarif-10m (10M/10M)" →
            belgi yoqildi → o'chirildi → maydonda "Siyosatni tanlang".

            ⚠️ Qiymat `useRef` da saqlanadi, `useState` da EMAS: uni
            saqlash qayta chizishni talab qilmaydi, `useState` esa har
            almashishda ortiqcha render berardi.
          */}
          <Switch
            onChange={(checked) => {
              if (checked) {
                // ⚠️ ANIQ tur berilgan: `getFieldValue` `any` qaytaradi va
                //    uni to'g'ridan-to'g'ri o'zlashtirish `eslint`
                //    (`no-unsafe-assignment`) da qizil beradi, `tsc` esa
                //    o'tkazib yuboradi — ya'ni bu FAQAT CI da ko'rinardi.
                //    (Generik ishlamaydi: bu antd versiyasida
                //    `getFieldValue` tur argumenti olmaydi.)
                previousPolicyId.current =
                    (form.getFieldValue('policyId') as string | null) ?? null
                form.setFieldsValue({ policyId: null })
                return
              }
              form.setFieldsValue({ policyId: previousPolicyId.current })
            }}
          />
        </Form.Item>

        <Form.Item
          name="policyId"
          label={t('catalog.plan.policy')}
          extra={t('catalog.plan.policyHint')}
          // ⚠️ Qo'shimcha paket bo'lmasa siyosat MAJBURIY — backenddagi
          // tekshiruv bilan bir xil qoida (`PlanServiceImpl.validatePlan`).
          // Usiz operator xatoni faqat 400 javobida ko'rardi.
          rules={addon ? [] : [{ required: true, message: t('catalog.plan.policyRequired') }]}
        >
          <Select
            allowClear
            // Qo'shimcha paketga siyosat biriktirib bo'lmaydi (backend 400
            // qaytaradi), shuning uchun maydon yopiladi.
            disabled={addon}
            loading={policies.isPending}
            placeholder={addon ? t('catalog.plan.noPolicy') : t('catalog.plan.policyRequired')}
            // ⚠️ Nom TARJIMA bilan — `PlansPage` jadvalidagi siyosat
            // ustuni bilan bir xil qoida. Ilgari bu yerda xom
            // `policy.name` turardi va operator jadvalda ko'rgan nomni
            // ro'yxatdan topa olmasdi. Zaxira o'zgarmagan: tarjima
            // bo'lmasa kanonik nom chiqadi, bo'sh HECH QACHON emas.
            options={(policies.data ?? []).map((policy) => ({
              value: policy.id,
              label: policyOptionLabel(policy, i18n.language),
            }))}
          />
        </Form.Item>

        {/*
          ⚠️ Bu izoh endi FAQAT qo'shimcha paketda chiqadi. U tezlikka
          tegmaydi: abonentning mavjud siyosati kuchda qoladi va faqat
          hajm/muddat qo'shiladi. Buni aytmaslik eng ko'p uchraydigan
          tushunmovchilik bo'lardi ("paket sotdim, tezlik o'zgarmadi").
        */}
        {addon ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('catalog.plan.quotaOnlyTitle')}
            description={t('catalog.plan.quotaOnlyHint')}
          />
        ) : null}

        <Form.Item name="billingKind" label={t('catalog.plan.billingKind')} rules={[{ required: true }]}>
          <Select
            options={BILLING_KINDS.map((k) => ({ value: k, label: t(`billingKind.${k}`) }))}
          />
        </Form.Item>

        <Form.Item name="price" label={t('catalog.plan.price')}>
          <MoneyInput currency={currency} />
        </Form.Item>

        <Form.Item name="quotaGb" label={t('catalog.plan.quotaGb')} extra={t('catalog.plan.quotaHint')}>
          <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder={t('billing.unlimited')} />
        </Form.Item>

        {/*
          ⚠️ VAQT KVOTASI — «AMAL MUDDATI» EMAS, VA IKKALASI CHALKASHTIRILADI.

            · `quotaSeconds` — abonent SOTIB OLGAN onlayn vaqt. Hotspot
              "1 soat" kartasi aynan shu: 3600. Sarflanmasa yonmaydi.
            · `validityDays` — kalendar muddat: shu kundan keyin qoldiq
              ham kuyadi.

          ⚠️ Maydon shu formada bo'lishi SHART: `PREPAID_TIME` turi aynan
          shu ro'yxatdan tanlanadi va backend undan vaqt chegarasi TALAB
          qiladi (`PlanServiceImpl`: `quotaSeconds`, `validityDays` yoki
          siyosat `sessionTimeout`). Maydonsiz operator `PREPAID_TIME`
          paketiga sotiladigan vaqtni umuman bera olmasdi.

          ⚠️ Soniya — DAQIQA/SOAT emas: aylantirish saqlashda mavjud
          qiymatni jimgina yaxlitlardi, katalogning qolgan vaqt maydonlari
          (`sessionTimeout`, `idleTimeout`) ham soniyada.
        */}
        <Form.Item
          name="quotaSeconds"
          label={t('catalog.plan.quotaSeconds')}
          extra={t('catalog.plan.quotaSecondsHint')}
        >
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            addonAfter={t('units.duration.s')}
            placeholder={t('billing.unlimited')}
          />
        </Form.Item>

        <Form.Item
          name="validityDays"
          label={t('catalog.plan.validityDays')}
          extra={t('catalog.plan.validityHint')}
        >
          <InputNumber min={1} style={{ width: '100%' }} placeholder={t('limits.noExpiration')} />
        </Form.Item>

        <Form.Item name="deliveryMethod" label={t('subscribers.columns.kind')} rules={[{ required: true }]}>
          <Select
            options={DELIVERY_METHODS.map((k) => ({ value: k, label: t(`deliveryMethod.${k}`) }))}
          />
        </Form.Item>

        <Form.Item name="state" label={t('subscribers.columns.status')} rules={[{ required: true }]}>
          <Select options={PLAN_STATES.map((s) => ({ value: s, label: t(`planState.${s}`) }))} />
        </Form.Item>

        <Form.Item name="isPublic" label={t('catalog.plan.isPublic')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
